import React, { useState } from 'react';
import { Card, Typography, Tooltip, Tag } from 'antd';
import { ToolOutlined, AppstoreOutlined, CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { ProjectStage } from '../../constants/stages';
import { STAGE_TOOLS } from '../../constants/tools/index';
import { tools } from '../../services/api';

const { Text } = Typography;

const SectionCard = styled(Card)`
  margin-bottom: 24px;
  background: transparent;
  border: none;
  
  .ant-card-body {
    padding: 0;
  }
`;

const GoalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GoalItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  background: linear-gradient(180deg, rgba(24, 144, 255, 0.08) 0%, rgba(24, 144, 255, 0.01) 100%);
  border-radius: 8px;
  overflow: hidden;
`;

const GoalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  user-select: none;
  padding: 16px 20px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .anticon {
    font-size: 16px;
    color: #666;
  }
`;

const ToolList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 240px);
  gap: 12px;
  padding: 16px 20px;
  transition: all 0.3s ease;
`;

const ToolCard = styled.a`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(24, 144, 255, 0.08);
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  width: 240px;
  height: 160px;
  justify-content: space-between;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.08);
    border-color: #1890ff;
  }
`;

const ToolHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const ToolIcon = styled.div<{ $hasImage: boolean }>`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 8px;
  color: #1890ff;
  font-size: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  ${props => !props.$hasImage && `
    background: #f5f5f5;
    border: 1px dashed #d9d9d9;
  `}
`;

const ToolInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToolTitle = styled.div`
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const ToolContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 8px;
`;

const ToolDescription = styled(Text)`
  color: #666;
  font-size: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  height: 32px;
  width: 100%;
  text-align: left;
  padding: 0 4px;
`;

const ToolTags = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  width: 100%;
`;

const ToolTag = styled(Tag)`
  margin: 0;
  flex-shrink: 0;
`;

const CollapsedContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  margin-bottom: ${props => props.$isOpen ? '0' : '0'};
`;

interface StageToolsProps {
  stage: ProjectStage;
}

const StageTools: React.FC<StageToolsProps> = ({ stage }) => {
  const [openCategories, setOpenCategories] = useState<number[]>([0]);

  const toggleCategory = (index: number) => {
    setOpenCategories(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleToolClick = async (tool: any) => {
    try {
      await tools.recordClick({
        tool_name: tool.name,
        tool_url: tool.url
      });
    } catch (error) {
      console.error('记录工具点击失败:', error);
    }
  };

  return (
    <SectionCard>
      <GoalList>
        {STAGE_TOOLS[stage]?.map((category, index) => (
          <GoalItem key={index}>
            <GoalTitle onClick={() => toggleCategory(index)}>
              {category.category}
              {openCategories.includes(index) ? <CaretDownOutlined /> : <CaretRightOutlined />}
            </GoalTitle>
            <CollapsedContent $isOpen={openCategories.includes(index)}>
              <ToolList>
                {category.tools?.map((tool, toolIndex) => (
                  <Tooltip key={toolIndex} title="点击访问工具网站">
                    <ToolCard 
                      href={tool.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={() => handleToolClick(tool)}
                    >
                      <ToolHeader>
                        <ToolIcon $hasImage={!!tool.icon}>
                          {tool.icon ? (
                            <img 
                              key={`${stage}-${tool.name}`}
                              src={`${tool.icon}?t=${stage}`}
                              alt={tool.name}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.parentElement?.classList.add('fallback');
                              }}
                            />
                          ) : (
                            <AppstoreOutlined />
                          )}
                        </ToolIcon>
                        <ToolInfo>
                          <ToolTitle>{tool.name}</ToolTitle>
                        </ToolInfo>
                      </ToolHeader>
                      <ToolContent>
                        <ToolDescription>{tool.description}</ToolDescription>
                      </ToolContent>
                      <ToolTags>
                        {tool.tags.map((tag, tagIndex) => (
                          <ToolTag key={tagIndex}>{tag}</ToolTag>
                        ))}
                      </ToolTags>
                    </ToolCard>
                  </Tooltip>
                ))}
              </ToolList>
            </CollapsedContent>
          </GoalItem>
        ))}
      </GoalList>
    </SectionCard>
  );
};

export default StageTools; 